import React, { useState, useRef, useEffect, useCallback, useId } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';

// Własny dropdown — natywny <select> na macOS/Safari rysuje listę systemowo i ignoruje
// `option { color/background }`, przez co opcje bywały niewidoczne. Tu lista to zwykłe divy.
// Lista renderowana w PORTALU (position:fixed nad triggerem), bo panele formularza to
// rodzeństwo z z-index:auto i kolejna sekcja przykrywałaby listę; dodatkowo w Kalendarzu
// select siedzi w modalu. Portal wychodzi ponad wszystko.

const Trigger = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: var(--sp-3) var(--sp-4);
  border: 2px solid ${p => (p.$open ? 'var(--primary)' : 'var(--input-border)')};
  border-radius: var(--r-md);
  background: var(--input-bg);
  color: var(--text);
  font-size: var(--fs-base);
  font-weight: 500;
  cursor: pointer;
  box-sizing: border-box;
  transition: all 0.18s ease;
  box-shadow: ${p => (p.$open ? '0 0 0 4px rgba(59,130,246,0.15)' : 'none')};
  &:hover { border-color: var(--primary); }
  &:disabled { cursor: not-allowed; opacity: 0.6; }
  &:disabled:hover { border-color: var(--input-border); }
  svg { color: var(--text-muted); flex-shrink: 0; transition: transform 0.18s ease; transform: rotate(${p => (p.$open ? '180deg' : '0')}); }
  span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
`;

const List = styled.ul`
  position: fixed;
  margin: 0;
  padding: var(--sp-2);
  list-style: none;
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: var(--r-md);
  box-shadow: 0 12px 32px var(--shadow-color, rgba(0,0,0,0.25));
  z-index: var(--z-popover);
  max-height: 260px;
  overflow-y: auto;
  box-sizing: border-box;
`;

const Item = styled.li`
  padding: var(--sp-3);
  border-radius: var(--r-sm);
  font-size: var(--fs-base);
  color: var(--text);
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  background: ${p => (p.$selected || p.$active ? 'var(--table-row-hover)' : 'transparent')};
  font-weight: ${p => (p.$selected ? 600 : 500)};
  /* Opcja podświetlona klawiaturą (aria-activedescendant) wygląda jak wskazana myszą. */
  ${p => (p.$active ? 'color: var(--primary); box-shadow: inset 0 0 0 1px var(--primary);' : '')}
  &:hover { background: var(--table-row-hover); color: var(--primary); }
`;

const Chevron = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

export default function SimpleSelect({ value, onChange, options = [], placeholder = 'Wybierz', style, className, disabled = false, 'aria-label': ariaLabel, 'data-cy': dataCy }) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  // Opcja podświetlona przy OTWARTEJ liście (strzałki ją przesuwają, Enter i spacja wybierają).
  const [aktywny, setAktywny] = useState(-1);
  const triggerRef = useRef(null);
  const listRef = useRef(null);
  const idListy = useId();
  const idOpcji = (i) => `${idListy}-opcja-${i}`;
  // Enter i spacja wybierające opcję nie mogą od razu otworzyć listy z powrotem kliknięciem
  // przycisku, które przeglądarka dokłada po klawiszu.
  const pominKlikRef = useRef(0);

  const place = useCallback(() => {
    if (triggerRef.current) setRect(triggerRef.current.getBoundingClientRect());
  }, []);

  useEffect(() => {
    if (!open || disabled) return undefined;
    place();
    const onDoc = (e) => {
      if (triggerRef.current && triggerRef.current.contains(e.target)) return;
      if (listRef.current && listRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    // Przewinięcie strony albo zmiana rozmiaru okna przesuwa przycisk, więc lista się zamyka
    // zamiast liczyć położenie na nowo. Przewinięcie WEWNĄTRZ samej listy (kółko myszy,
    // przewinięcie do podświetlonej opcji) przycisku nie rusza i listy nie zamyka. Nasłuch w fazie
    // przechwytywania łapał dawniej także je, więc lista z wybraną opcją dalej niż piąta zamykała
    // się zaraz po otwarciu, a strzałka wychodząca poza widoczne opcje zamykała ją w trakcie
    // wybierania (sprawdzenie 12.10.2026). Przy 'resize' celem jest window, które nie jest węzłem.
    const onMove = (e) => {
      const lista = listRef.current;
      if (e && e.type === 'scroll' && lista && e.target instanceof Node && lista.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, disabled, place]);

  const selected = options.find((o) => String(o.value) === String(value));
  const indeksWybranej = options.findIndex((o) => String(o.value) === String(value));

  // Otwarcie: podświetlona jest bieżąca wartość, a położenie liczone od przycisku teraz. Oba
  // ustawiane RAZEM z otwarciem, nie w efekcie po nim: dawniej pierwszy render listy miał
  // podświetlenie i położenie z POPRZEDNIEGO otwarcia, więc lista przewijała się do starej opcji
  // (sprawdzenie 12.10.2026).
  const otworz = () => {
    setAktywny(indeksWybranej >= 0 ? indeksWybranej : 0);
    place();
    setOpen(true);
  };

  // Podświetlona opcja zawsze widoczna. Przewijana jest WYŁĄCZNIE sama lista (scrollTop), nie
  // jej przodkowie, jak przy scrollIntoView, które potrafi przewinąć też stronę.
  useEffect(() => {
    const lista = listRef.current;
    if (!open || aktywny < 0 || !lista) return;
    const el = lista.querySelector(`[id="${idListy}-opcja-${aktywny}"]`);
    if (!el) return;
    const gora = el.offsetTop;
    const dol = gora + el.offsetHeight;
    if (gora < lista.scrollTop) lista.scrollTop = gora;
    else if (dol > lista.scrollTop + lista.clientHeight) lista.scrollTop = dol - lista.clientHeight;
  }, [open, aktywny, rect, idListy]);

  // Klawiatura jak w systemowej liście (test odbiorczy 11.10.2026: kroju i rozmiaru pisma w
  // konstruktorach nie dało się wybrać bez myszy). Przy ZAMKNIĘTEJ liście strzałki wybierają
  // poprzednią/następną opcję, Home i End pierwszą i ostatnią; Enter i spacja otwierają listę.
  // Przy OTWARTEJ liście strzałki, Home i End tylko przesuwają podświetlenie, a Enter i spacja
  // wybierają podświetloną opcję i zamykają listę (ponowny test 11.10.2026: strzałka przy otwartej
  // liście od razu zmieniała wartość, a lista zostawała otwarta). Drugi argument onChange mówi,
  // że wybór przyszedł z klawiatury: pasek edytora umowy nie oddaje wtedy fokusu tekstowi.
  const wybierzIndeks = (n) => {
    const opcja = options[n];
    if (opcja && String(opcja.value) !== String(value)) onChange(opcja.value, { zKlawiatury: true });
  };
  const naKlawisz = (e) => {
    if (disabled || !options.length) return;
    const ostatni = options.length - 1;
    if (open) {
      let n = null;
      if (e.key === 'ArrowDown') n = aktywny < 0 ? 0 : Math.min(ostatni, aktywny + 1);
      else if (e.key === 'ArrowUp') n = aktywny < 0 ? ostatni : Math.max(0, aktywny - 1);
      else if (e.key === 'Home') n = 0;
      else if (e.key === 'End') n = ostatni;
      if (n !== null) {
        e.preventDefault();
        setAktywny(n);
        return;
      }
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        pominKlikRef.current = Date.now();
        if (aktywny >= 0) wybierzIndeks(aktywny);
        setOpen(false);
        return;
      }
      if (e.key === 'Tab') setOpen(false);
      return;
    }
    let n = null;
    if (e.key === 'ArrowDown') n = indeksWybranej < 0 ? 0 : Math.min(ostatni, indeksWybranej + 1);
    else if (e.key === 'ArrowUp') n = indeksWybranej < 0 ? ostatni : Math.max(0, indeksWybranej - 1);
    else if (e.key === 'Home') n = 0;
    else if (e.key === 'End') n = ostatni;
    if (n === null) return;
    e.preventDefault();
    wybierzIndeks(n);
  };

  return (
    <div style={{ display: 'inline-block', boxSizing: 'border-box', ...style }} className={className}>
      <Trigger
        ref={triggerRef}
        type="button"
        $open={open}
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          if (Date.now() - pominKlikRef.current < 500) return;
          if (open) setOpen(false);
          else otworz();
        }}
        onKeyDown={naKlawisz}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? idListy : undefined}
        aria-activedescendant={open && aktywny >= 0 ? idOpcji(aktywny) : undefined}
        aria-label={ariaLabel}
        data-cy={dataCy}
      >
        <span>{selected ? selected.label : placeholder}</span>
        <Chevron />
      </Trigger>
      {open && rect && ReactDOM.createPortal(
        <List
          ref={listRef}
          id={idListy}
          role="listbox"
          aria-label={ariaLabel}
          style={{ top: rect.bottom + 6, left: rect.left, minWidth: rect.width }}
        >
          {options.map((o, i) => (
            <Item
              key={String(o.value)}
              id={idOpcji(i)}
              role="option"
              aria-selected={String(o.value) === String(value)}
              $selected={String(o.value) === String(value)}
              $active={i === aktywny}
              onMouseEnter={() => setAktywny(i)}
              onClick={() => { onChange(o.value); setOpen(false); }}
              data-cy={dataCy ? `${dataCy}-option-${o.value}` : undefined}
            >
              {o.label}
            </Item>
          ))}
        </List>,
        document.body
      )}
    </div>
  );
}
